import {
  getListCaptionStylesQueryKey,
  useCreateCaptionStyle,
  useUpdateCaptionStyle,
} from '@reelplicate/api-client/caption-styles';
import type { CaptionStyleDto } from '@reelplicate/api-client/model';
import {
  CAPTION_FONTS,
  CAPTION_PRESETS,
  type CaptionFontId,
  type CaptionPosition,
  type CaptionStyle,
  captionStyleSchema,
} from '@reelplicate/flows';
import {
  Button,
  ColorField,
  Field,
  Inline,
  Modal,
  ModalContent,
  NumberStepper,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectItem,
  SliderField,
  Stack,
  TextInput,
  toast,
  Toggle,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { CaptionPreview } from './caption-preview';
import { SAMPLE_CAPTION } from './caption-style-options';

const round = (n: number, step = 100) => Math.round(n * step) / step;

/** What the editor starts from: a style to edit, or a look to build a new one on. */
export type EditorStart =
  | { mode: 'create'; name?: string; style: CaptionStyle }
  | { mode: 'edit'; id: string; name: string; style: CaptionStyle };

/**
 * Figma "Caption style editor" (398:2994): build a caption style with a live
 * preview on the slides' own images, then save it to the workspace (Brand
 * profile → Caption styles) for every flow to use.
 */
export function StyleEditor({
  workspaceId,
  start,
  images = [],
  onOpenChange,
  onSaved,
}: {
  workspaceId: string;
  /** Open when set. */
  start: EditorStart | null;
  /** Images to preview on (the slide pools' first few). */
  images?: string[];
  onOpenChange: (open: boolean) => void;
  onSaved?: (style: CaptionStyleDto) => void;
}) {
  const queryClient = useQueryClient();
  const create = useCreateCaptionStyle();
  const update = useUpdateCaptionStyle();
  const [name, setName] = useState('');
  const [style, setStyle] = useState<CaptionStyle>(CAPTION_PRESETS[0]!.style);
  const [sample, setSample] = useState(SAMPLE_CAPTION);
  const [position, setPosition] = useState<CaptionPosition>('bottom');
  const [image, setImage] = useState(0);

  // A new start (opening the editor) resets the form.
  const [shown, setShown] = useState<EditorStart | null>(null);
  if (start !== shown) {
    setShown(start);
    if (start) {
      setName(start.name ?? '');
      setStyle(start.style);
    }
  }

  const set = (patch: Partial<CaptionStyle>) => setStyle((s) => ({ ...s, ...patch }));
  const setIn = <K extends 'background' | 'outline' | 'shadow' | 'highlight'>(
    key: K,
    patch: Partial<CaptionStyle[K]>,
  ) => setStyle((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const saving = create.isPending || update.isPending;
  const save = async () => {
    const parsed = captionStyleSchema.safeParse(style);
    if (!name.trim() || !parsed.success || !start) return;
    try {
      const saved =
        start.mode === 'edit'
          ? await update.mutateAsync({
              id: workspaceId,
              styleId: start.id,
              data: { name: name.trim(), style: parsed.data },
            })
          : await create.mutateAsync({
              id: workspaceId,
              data: { name: name.trim(), style: parsed.data },
            });
      await queryClient.invalidateQueries({
        queryKey: getListCaptionStylesQueryKey(workspaceId),
      });
      toast.success(
        start.mode === 'edit' ? 'Style saved' : `“${saved.name}” added to Brand profile`,
      );
      onSaved?.(saved);
      onOpenChange(false);
    } catch {
      // Shown as a toast (a taken name, say); the editor stays open.
    }
  };

  return (
    <Modal open={start !== null} onOpenChange={onOpenChange}>
      <ModalContent
        size="lg"
        title={start?.mode === 'edit' ? 'Edit caption style' : 'New caption style'}
        description="Build a look once, then use it on any slide in any flow."
        className="w-295"
        footerStart={
          <span className="text-caption text-text-secondary">
            Saved to Brand profile, so every flow can use it.
          </span>
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button loading={saving} disabled={!name.trim()} onClick={() => void save()}>
              Save style
            </Button>
          </>
        }
      >
        <div className="@container max-h-[70vh] overflow-x-hidden overflow-y-auto">
          <div className="grid grid-cols-1 gap-8 @2xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] @5xl:grid-cols-[400px_minmax(0,1fr)]">
            <Stack gap={4} className="self-start @2xl:sticky @2xl:top-0">
              <CaptionPreview
                style={style}
                text={sample}
                position={position}
                imageUrl={images[image]}
                label="Live preview"
                className="mx-auto max-w-100 rounded-xl"
              />
              {images.length > 1 && (
                <Inline gap={2} alignY="center">
                  <span className="text-label-sm text-text-secondary">Preview on</span>
                  {images.slice(0, 4).map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      aria-label={`Preview on image ${i + 1}`}
                      aria-pressed={i === image}
                      onClick={() => setImage(i)}
                      className={`size-10 overflow-hidden rounded-sm border-2 ${i === image ? 'border-brand-primary' : 'border-transparent'}`}
                    >
                      <img src={url} alt="" className="size-full object-cover" />
                    </button>
                  ))}
                </Inline>
              )}
              <Field
                label="Sample text"
                helper="Use *asterisks* to try the keyword highlight."
              >
                <TextInput value={sample} onChange={(e) => setSample(e.target.value)} />
              </Field>
              <Field label="Text sits at">
                <SegmentedControl
                  aria-label="Text sits at"
                  value={position}
                  onValueChange={(v) => setPosition(v as CaptionPosition)}
                >
                  <SegmentedControlItem value="top">Top</SegmentedControlItem>
                  <SegmentedControlItem value="center">Middle</SegmentedControlItem>
                  <SegmentedControlItem value="bottom">Bottom</SegmentedControlItem>
                </SegmentedControl>
              </Field>
            </Stack>

            <Stack
              gap={6}
              // Phone-width modals: tighter segments so the four shadow kinds still fit.
              className="@max-md:[&_[data-slot=segmented-control]>*]:px-3.5"
            >
              <Group title="Style">
                <Row>
                  <Field label="Name">
                    <TextInput
                      value={name}
                      maxLength={40}
                      placeholder="e.g. Bloom serif"
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Field>
                  <Field label="Start from">
                    <Select
                      placeholder="A preset"
                      onValueChange={(id) => {
                        const preset = CAPTION_PRESETS.find((p) => p.id === id);
                        if (preset) setStyle(preset.style);
                      }}
                    >
                      {CAPTION_PRESETS.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {`${p.source === 'tiktok' ? 'TikTok' : 'Instagram'} · ${p.name}`}
                        </SelectItem>
                      ))}
                    </Select>
                  </Field>
                </Row>
              </Group>

              <Group title="Text">
                <Row>
                  <Field label="Font">
                    <Select
                      value={style.font}
                      onValueChange={(font) => set({ font: font as CaptionFontId })}
                    >
                      {CAPTION_FONTS.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Colour">
                    <ColorField
                      aria-label="Text colour"
                      value={style.color}
                      onValueChange={(color) => set({ color })}
                    />
                  </Field>
                </Row>
                <Row>
                  <Field label="Size (% of width)">
                    <NumberStepper
                      size="sm"
                      value={style.size}
                      min={3}
                      max={12}
                      step={0.5}
                      onValueChange={(v) => v !== null && set({ size: v })}
                    />
                  </Field>
                  <Field label="Case">
                    <SegmentedControl
                      aria-label="Case"
                      value={style.case}
                      onValueChange={(v) => set({ case: v as CaptionStyle['case'] })}
                    >
                      <SegmentedControlItem value="none">Aa</SegmentedControlItem>
                      <SegmentedControlItem value="upper">AA</SegmentedControlItem>
                      <SegmentedControlItem value="lower">aa</SegmentedControlItem>
                    </SegmentedControl>
                  </Field>
                </Row>
                <Row>
                  <Field label="Letter spacing">
                    <NumberStepper
                      size="sm"
                      value={round(style.letterSpacing * 100)}
                      min={-10}
                      max={50}
                      step={1}
                      onValueChange={(v) => v !== null && set({ letterSpacing: v / 100 })}
                    />
                  </Field>
                  <Field label="Line height">
                    <NumberStepper
                      size="sm"
                      value={style.lineHeight}
                      min={0.9}
                      max={2}
                      step={0.05}
                      onValueChange={(v) => v !== null && set({ lineHeight: round(v) })}
                    />
                  </Field>
                </Row>
              </Group>

              <Group title="Background">
                <Row>
                  <Field label="Shape">
                    <SegmentedControl
                      aria-label="Background shape"
                      value={style.background.shape}
                      onValueChange={(v) =>
                        setIn('background', {
                          shape: v as CaptionStyle['background']['shape'],
                        })
                      }
                    >
                      <SegmentedControlItem value="none">None</SegmentedControlItem>
                      <SegmentedControlItem value="line">Per line</SegmentedControlItem>
                      <SegmentedControlItem value="block">Block</SegmentedControlItem>
                    </SegmentedControl>
                  </Field>
                  <Field label="Colour">
                    <ColorField
                      aria-label="Background colour"
                      value={style.background.color}
                      onValueChange={(color) => setIn('background', { color })}
                    />
                  </Field>
                </Row>
                {style.background.shape !== 'none' && (
                  <Row>
                    <SliderField
                      label="Opacity"
                      min={0}
                      max={100}
                      minLabel="0%"
                      maxLabel="100%"
                      formatValue={(v) => `${v}%`}
                      value={[Math.round(style.background.opacity * 100)]}
                      onValueChange={([v]) =>
                        setIn('background', { opacity: (v ?? 100) / 100 })
                      }
                    />
                    <Field label="Roundness">
                      <NumberStepper
                        size="sm"
                        value={round(style.background.radius * 100)}
                        min={0}
                        max={100}
                        step={2}
                        onValueChange={(v) =>
                          v !== null && setIn('background', { radius: v / 100 })
                        }
                      />
                    </Field>
                    <Field label="Padding">
                      <NumberStepper
                        size="sm"
                        value={round(style.background.padding * 100)}
                        min={0}
                        max={150}
                        step={2}
                        onValueChange={(v) =>
                          v !== null && setIn('background', { padding: v / 100 })
                        }
                      />
                    </Field>
                  </Row>
                )}
              </Group>

              <Group title="Outline & shadow">
                <Row>
                  <Field label="Outline">
                    <Toggle
                      label={style.outline.width > 0 ? 'On' : 'Off'}
                      checked={style.outline.width > 0}
                      onCheckedChange={(on) => setIn('outline', { width: on ? 0.14 : 0 })}
                    />
                  </Field>
                  {style.outline.width > 0 && (
                    <>
                      <Field label="Thickness">
                        <NumberStepper
                          size="sm"
                          value={round(style.outline.width * 100)}
                          min={2}
                          max={40}
                          step={1}
                          onValueChange={(v) =>
                            v !== null && setIn('outline', { width: v / 100 })
                          }
                        />
                      </Field>
                      <Field label="Outline colour">
                        <ColorField
                          aria-label="Outline colour"
                          value={style.outline.color}
                          onValueChange={(color) => setIn('outline', { color })}
                        />
                      </Field>
                    </>
                  )}
                </Row>
                <Row>
                  <Field label="Shadow">
                    <SegmentedControl
                      aria-label="Shadow"
                      value={style.shadow.kind}
                      onValueChange={(v) =>
                        setIn('shadow', {
                          kind: v as CaptionStyle['shadow']['kind'],
                          opacity: style.shadow.opacity || 0.6,
                        })
                      }
                    >
                      <SegmentedControlItem value="none">None</SegmentedControlItem>
                      <SegmentedControlItem value="soft">Soft</SegmentedControlItem>
                      <SegmentedControlItem value="hard">Hard</SegmentedControlItem>
                      <SegmentedControlItem value="glow">Glow</SegmentedControlItem>
                    </SegmentedControl>
                  </Field>
                  {style.shadow.kind !== 'none' && (
                    <Field label="Shadow colour">
                      <ColorField
                        aria-label="Shadow colour"
                        value={style.shadow.color}
                        onValueChange={(color) => setIn('shadow', { color })}
                      />
                    </Field>
                  )}
                </Row>
              </Group>

              <Group title="Layout & highlight">
                <Row>
                  <Field label="Align">
                    <SegmentedControl
                      aria-label="Align"
                      value={style.align}
                      onValueChange={(v) => set({ align: v as CaptionStyle['align'] })}
                    >
                      <SegmentedControlItem value="left">Left</SegmentedControlItem>
                      <SegmentedControlItem value="center">Centre</SegmentedControlItem>
                      <SegmentedControlItem value="right">Right</SegmentedControlItem>
                    </SegmentedControl>
                  </Field>
                  <SliderField
                    label="Text width"
                    min={40}
                    max={95}
                    minLabel="40%"
                    maxLabel="95%"
                    formatValue={(v) => `${v}%`}
                    value={[Math.round(style.width * 100)]}
                    onValueChange={([v]) => set({ width: (v ?? 86) / 100 })}
                  />
                </Row>
                <Row>
                  <Field
                    label="Keyword highlight"
                    helper="Words in *asterisks* in a caption."
                  >
                    <Toggle
                      label={style.highlight.enabled ? 'On' : 'Off'}
                      checked={style.highlight.enabled}
                      onCheckedChange={(enabled) => setIn('highlight', { enabled })}
                    />
                  </Field>
                  {style.highlight.enabled && (
                    <Field label="Highlight colour">
                      <ColorField
                        aria-label="Highlight colour"
                        value={style.highlight.color}
                        onValueChange={(color) => setIn('highlight', { color })}
                      />
                    </Field>
                  )}
                </Row>
              </Group>
            </Stack>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack as="section" gap={3} aria-label={title}>
      <h3 className="text-overline text-text-tertiary uppercase">{title}</h3>
      {children}
    </Stack>
  );
}

/**
 * Fields share a line equally and wrap once they no longer fit; a segmented
 * control can't shrink, so its field is never narrower than the control.
 */
function Row({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-4 *:min-w-0 *:flex-[1_1_13rem] *:has-data-[slot=segmented-control]:min-w-fit">
      {children}
    </div>
  );
}
